import { DateUtils } from '@/services/DateFormat'
import userInfoStore from '@/store/userInfo'
import { UNDEFINED_ENTITY_ID } from '../Catalogs'
import { BLUEError } from '../Errors'
import { NUMBER, STRING, TIME } from '../GeneralConstants'
import store from '@/store'
import { AttachmentFormat } from './Attachment'
enum AsyncTaskType {
  NONE = 'NONE',
  DATAFEED = 'DATAFEED',
  INTEGRATION = 'INTEGRATION',
  NORMALIZATION = 'NORMALIZATION',
}

enum AsyncTaskStatus {
  NEW = 'NEW',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR',
  WAITING = 'WAITING',
  CANCELED = 'CANCELED',
  BLACKLIST = 'BLACKLIST'
}

enum AsyncTaskFormat {
  CSV = 'CSV',
  TSV = 'TSV',
  XLSX = 'XLSX',
  TXT = 'TXT',
  ZIP = 'ZIP'
}

enum AsynTaskHeaders {
  ID = 'ID',
  LABEL = 'LABEL'
}

class TemplateDetail {
  id: number
  name: string
  label: string
  version: number
}

interface AttachmentProcess {
  total: number,
  fetched: number
}

class AsyncTaskProcess {
  requestId: string
  messageId: string
  total: number
  fetched?: number
  processed?: number
  successful?: number
  format: AsyncTaskFormat = AsyncTaskFormat.CSV
  attachmentIds?: string[]
  attachmentFormat?: AttachmentFormat
  attachmentProgress?: AttachmentProcess
  header?: string = AsynTaskHeaders.LABEL
  search?: number = -1
  error?: BLUEError
  timeZone?: string // format (+|-)dd:dd
  processWorked?: boolean
  source?: string

  static getHeaderTranslate (header: AsynTaskHeaders): string {
    if (header === AsynTaskHeaders.ID) {
      return 'Nombre'
    } else if (header === AsynTaskHeaders.LABEL) {
      return 'Descripción'
    } else {
      return header
    }
  }
}

class AsyncTask {
  id: number
  name: string
  user: number
  userName: string
  status: AsyncTaskStatus
  type: AsyncTaskType
  process: AsyncTaskProcess
  template: TemplateDetail
  creationDate: string
  updateDate: string
  duration: string
  percentage: number
  remainingTime?: string
  attachmentPercentage?: number
  attachmentRemainingTime?: string

  static getInstance () {
    const asyncTask = new AsyncTask()
    asyncTask.id = UNDEFINED_ENTITY_ID
    asyncTask.name = STRING.EMPTY_STRING
    asyncTask.user = userInfoStore.userInfo.id
    asyncTask.status = AsyncTaskStatus.NEW
    asyncTask.type = AsyncTaskType.DATAFEED
    asyncTask.process = new AsyncTaskProcess()
    asyncTask.template = new TemplateDetail()
    return asyncTask
  }

  static createInstance (params: AsyncTask) {
    const task = new AsyncTask()
    task.id = params.id
    task.name = params.name
    task.user = params.user
    task.status = params.status
    task.type = params.type
    task.process = params.process
    task.template = params.template
    task.creationDate = params.creationDate
    task.updateDate = params.updateDate
    task.process = params.process
    // Duration to complete the task
    task.duration = task.getDuration(new Date(task.creationDate), new Date(task.updateDate))
    // Advance percentage
    task.percentage = task.getPercentage()
    // Advance percentage
    task.attachmentPercentage = task.getAttachmentPercentage()
    // Error translate
    const error = params.process.error
    if (error && error.code) {
      task.process.error!.description = error.description
    }
    return task
  }

  /**
   * Set the time remaining to complete the target task
   * @param target task to set the time remaining to complete
   * @param oldTask last version of the task
  */
  static setRemainingTimeBetweenTasks (target: AsyncTask, oldTask: AsyncTask): void {
    const lastUpdate = new Date(oldTask.updateDate)
    const currentDate = new Date(target.updateDate)
    const timeLapse = currentDate.getTime() - lastUpdate.getTime()
    // Datafeed or Integration cases, to add a new one, change the ternary to if's
    const fillsFetched = target.type === AsyncTaskType.DATAFEED
      ? (target.process.fetched ?? 0) - (oldTask.process.fetched ?? 0)
      : (target.process.processed ?? 0) - (oldTask.process.processed ?? 0)
    const remainingFills = target.type === AsyncTaskType.DATAFEED
      ? target.process.total - (target.process.fetched ?? 0)
      : target.process.total - (target.process.processed ?? 0)

    const remainingTimeInMilliseconds = fillsFetched > 0
      ? remainingFills * timeLapse / fillsFetched
      : NUMBER.MINUS_ONE
    target.remainingTime = remainingTimeInMilliseconds === NUMBER.MINUS_ONE ||
    remainingTimeInMilliseconds === NUMBER.ZERO
      ? oldTask.remainingTime ?? '...'
      : DateUtils.getTimeLapseDHMSFromMilliseconds(remainingTimeInMilliseconds)
  }

  /**
   * Set the remaining time to complete the process of get the attachments
   * This is calculate based on a current task values against it's previous values,
   * and the time is setting in the 'target' task
   *
   * @param target task to set the time remaining to complete
   * @param oldTask last version of the task
  */
  static setRemainingAttachmentTimeBetweenTasks (target: AsyncTask, oldTask: AsyncTask): void {
    const lastUpdate = new Date(oldTask.updateDate)
    const currentDate = new Date(target.updateDate)
    const timeLapse = DateUtils.differenceInMilliseconds(lastUpdate, currentDate)
    const targetAttachmentProgress = target.process.attachmentProgress
    const oldAttachmentProgress = oldTask.process.attachmentProgress
    if (targetAttachmentProgress && oldAttachmentProgress) {
      const fillsFetched = (targetAttachmentProgress.fetched ?? 0) - (oldAttachmentProgress.fetched ?? 0)
      const remainingFills = targetAttachmentProgress.total - (targetAttachmentProgress.fetched ?? 0)
      const remainingTimeInMilliseconds = fillsFetched > 0
        ? remainingFills * timeLapse / fillsFetched
        : NUMBER.MINUS_ONE
      target.attachmentRemainingTime = remainingTimeInMilliseconds === NUMBER.MINUS_ONE ||
      remainingTimeInMilliseconds === NUMBER.ZERO
        ? oldTask.attachmentRemainingTime ?? '...'
        : DateUtils.getTimeLapseDHMSFromMilliseconds(remainingTimeInMilliseconds)
    }
  }

  /**
   * @returns true, if the task is of DATAFEED type
   * otherwise false
   */
  isDatafeedTask (): boolean {
    return this.type === AsyncTaskType.DATAFEED
  }

  /**
   * @returns true, if the task is of INTEGRATION type
   * otherwise false
   */
  isIntegrationTask (): boolean {
    return this.type === AsyncTaskType.INTEGRATION
  }

  /**
   * Get the time to take to finished the task.
   *
   * @param from creation date
   * @param to update date
   */
  getDuration (from: Date, to: Date): string {
    const timeLapse = DateUtils.differenceInMilliseconds(from, to)
    return DateUtils.getTimeLapseDHMSFromMilliseconds(timeLapse)
  }

  /**
   * @returns the percentage that has been processed the task
   */
  getPercentage (): number {
    let percentage = 0
    if (this.isDatafeedTask()) {
      percentage = this.process.fetched ? this.process.fetched * 100 / this.process.total : 0
    } else if (this.isIntegrationTask()) {
      percentage = this.process.processed ? this.process.processed * 100 / this.process.total : 0
    }
    return percentage
  }

  /**
   * @returns the percentage that has been processed in the attahment process
   */
  getAttachmentPercentage (): number {
    let percentage = 0
    if (this.isDatafeedTask()) {
      const attachmentProgress = this.process.attachmentProgress
      percentage = attachmentProgress && attachmentProgress.total
        ? attachmentProgress.fetched * NUMBER.ONE_HUNDRED / attachmentProgress.total
        : NUMBER.ZERO
    }
    return percentage
  }

  /**
   * @returns the translation for the task status
   */
  taskStatusTranslate (): string {
    const status = this.status
    let translation = ''
    if (status === AsyncTaskStatus.NEW) {
      translation = 'Nueva'
    } else if (status === AsyncTaskStatus.PROCESSING) {
      translation = 'En proceso'
    } else if (status === AsyncTaskStatus.WAITING) {
      translation = 'En espera'
    } else if (status === AsyncTaskStatus.DONE) {
      translation = 'Terminada'
    } else if (status === AsyncTaskStatus.ERROR) {
      translation = 'Error'
    } else if (status === AsyncTaskStatus.CANCELED) {
      translation = 'Cancelada'
    }
    return translation
  }

  taskType (): string {
    let type = ''
    if (this.type === AsyncTaskType.DATAFEED) type = 'Mis tareas'
    if (this.type === AsyncTaskType.INTEGRATION) type = 'Integración'
    return type
  }

  /**
   * Get a property instance from a task
   *
   * @param task instance to get a property
   * @param name of the property to get
   * @returns an any value from the protperty
   */
  static getPropByName (task: AsyncTask, name: string) {
    return task[name as keyof AsyncTask]
  }

  getLocalCreationDate (): string {
    return DateUtils.fromDatetimeInUTCToLocalFormat(this.creationDate)
  }

  getLocalUpdateDate (): string {
    return DateUtils.fromDatetimeInUTCToLocalFormat(this.updateDate)
  }

  availableResultsLink (): boolean {
    const updateDate = this.updateDate
    const updateDateInUTC = DateUtils.isUTCFormat(updateDate)
      ? updateDate
      : DateUtils.addZToDatetime(updateDate)
    const lastModification = new Date(updateDateInUTC)
    const today = new Date()
    const aliveTaskInMilliseconds = today.getTime() - lastModification.getTime()
    return aliveTaskInMilliseconds < (NUMBER.SIX * TIME.MILLISECONDS_IN_AN_HOUR)
  }
  isTaskInBlacklistNotifications () : boolean {
    return store.getters['taskNotifications/blacklistTasks'].some((task: any) => task.id === this.id)
  }
}

class AsyncTaskFilters {
  name?: string = undefined
  type?: AsyncTaskType = AsyncTaskType.NONE
  status?: AsyncTaskStatus = undefined
  template?: number = undefined
  domain?: number = undefined
  project?: number = undefined
  user?: number = undefined
}

export {
  AsyncTask,
  AsyncTaskType,
  AsyncTaskStatus,
  AsyncTaskFormat,
  AsyncTaskProcess,
  AsyncTaskFilters,
  AsynTaskHeaders
}
